import { useState, useEffect, useLayoutEffect, useCallback, Fragment } from 'react'
import { createPortal } from 'react-dom'
import { Switch } from '@headlessui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid'

import ExerciseBlock from 'components/Program/ExerciseBlocks/ExerciseBlock'

const INITIAL_MOUNT_Y_OFFSET = 25
const WORKOUT_CONFIG_CARD_WIDTH = 320
const WORKOUT_PHASE_CARD_GAP = 24
const EXERCISE_BLOCK_CARD_WIDTH = 1000
const EXERCISE_BLOCK_CARD_GAP = 24
const CANVAS_COLLAPSED_CARD_HEIGHT = 58
const CANVAS_EXERCISE_BLOCK_DESCRIPTION_LINE_HEIGHT = 16
const WORKOUT_PHASE_TYPE_SECTION_GAP = 96
const RELATION_COLUMN_GAP = 160
const CANVAS_CONNECTOR_DEFAULT_COLOR = '#a1a1aa'
const CANVAS_CONNECTOR_ACTIVE_COLOR = '#fb923c'

function estimateWorkoutPhaseConfigHeight(workoutPhaseConfig, isExpanded = true) {
    if (!isExpanded) {
        return CANVAS_COLLAPSED_CARD_HEIGHT
    }

    const headerHeight = CANVAS_COLLAPSED_CARD_HEIGHT
    const bodyPadding = 32
    const slotHeight = 36
    const slotGap = 8

    const slots = workoutPhaseConfig.workoutSlotConfigs ?? []
    const slotsHeight = slots.length === 0
        ? 0
        : slots.length * slotHeight + Math.max(0, slots.length - 1) * slotGap

    return headerHeight + bodyPadding + slotsHeight
}

function estimateExerciseBlockCollapsedHeight(exerciseBlock) {
    if (exerciseBlock?.description) {
        return CANVAS_COLLAPSED_CARD_HEIGHT + CANVAS_EXERCISE_BLOCK_DESCRIPTION_LINE_HEIGHT
    }

    return CANVAS_COLLAPSED_CARD_HEIGHT
}

function orthogonalConnectorPath(x1, y1, x2, y2) {
    const midX = x1 + (x2 - x1) / 2
    return `M ${x1} ${y1} L ${midX} ${y1} L ${midX} ${y2} L ${x2} ${y2}`
}

function getSortedWorkoutPhaseTypes(workoutPhaseTypes, workoutPhaseTypeHashMap) {
    if (Array.isArray(workoutPhaseTypes) && workoutPhaseTypes.length > 0) {
        return [...workoutPhaseTypes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    }

    return Object.entries(workoutPhaseTypeHashMap ?? {})
        .map(([workoutPhaseTypeId, workoutPhaseType]) => ({
            workoutPhaseTypeId,
            ...workoutPhaseType,
        }))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
}

function compareBySortOrder(a, b) {
    return (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
}

function splitUnattachedExerciseBlock(exerciseBlocks) {
    const unattachedExerciseBlock = (exerciseBlocks ?? []).find((exerciseBlock) => (
        exerciseBlock?.exerciseBlockId === 'unattached'
    )) ?? null
    const attachedExerciseBlocks = (exerciseBlocks ?? []).filter((exerciseBlock) => (
        exerciseBlock?.exerciseBlockId !== 'unattached'
    ))

    return { unattachedExerciseBlock, attachedExerciseBlocks }
}

function buildExercisesViewLayout(
    workoutConfigs,
    exerciseBlocks,
    exerciseBlockMetrics,
    expandedWorkoutPhaseConfigId,
    workoutPhaseTypes,
    workoutPhaseTypeHashMap,
) {
    const allPhaseConfigs = []
    workoutConfigs.forEach((workoutConfig) => {
        (workoutConfig.workoutPhaseConfigs ?? []).forEach((workoutPhaseConfig, workoutPhaseConfigIndex) => {
            allPhaseConfigs.push({
                workoutConfig,
                workoutPhaseConfig,
                workoutPhaseConfigIndex,
            })
        })
    })

    const { unattachedExerciseBlock, attachedExerciseBlocks } = splitUnattachedExerciseBlock(exerciseBlocks)
    const sortedWorkoutPhaseTypes = getSortedWorkoutPhaseTypes(workoutPhaseTypes, workoutPhaseTypeHashMap)
    const assignedTypeIds = new Set(sortedWorkoutPhaseTypes.map((workoutPhaseType) => workoutPhaseType.workoutPhaseTypeId))
    const leftoverPhases = allPhaseConfigs.filter((phase) => (
        !assignedTypeIds.has(phase.workoutPhaseConfig.workoutPhaseTypeId)
    ))
    const leftoverBlocks = attachedExerciseBlocks.filter((exerciseBlock) => (
        !assignedTypeIds.has(exerciseBlock.workoutPhaseTypeId)
    ))

    const typesToRender = [...sortedWorkoutPhaseTypes]
    if (leftoverPhases.length > 0 || leftoverBlocks.length > 0) {
        typesToRender.push({
            workoutPhaseTypeId: '__unknown__',
            name: 'Unknown',
            sortOrder: Number.MAX_SAFE_INTEGER,
        })
    }

    const sectionWidth = WORKOUT_CONFIG_CARD_WIDTH + RELATION_COLUMN_GAP + EXERCISE_BLOCK_CARD_WIDTH
    const phaseOriginX = 0
    const blockOriginX = WORKOUT_CONFIG_CARD_WIDTH + RELATION_COLUMN_GAP
    const hasExpandedPhase = expandedWorkoutPhaseConfigId != null

    const workoutPhaseTypeSectionLayouts = []
    const connectors = []
    let sectionOffsetY = 0

    typesToRender.forEach((workoutPhaseType) => {
        const isUnknown = workoutPhaseType.workoutPhaseTypeId === '__unknown__'
        const typePhases = (isUnknown
            ? leftoverPhases
            : allPhaseConfigs.filter((phase) => (
                phase.workoutPhaseConfig.workoutPhaseTypeId === workoutPhaseType.workoutPhaseTypeId
            ))
        ).slice().sort((a, b) => compareBySortOrder(a.workoutPhaseConfig, b.workoutPhaseConfig))

        const typeBlocks = (isUnknown
            ? leftoverBlocks
            : attachedExerciseBlocks.filter((exerciseBlock) => (
                exerciseBlock.workoutPhaseTypeId === workoutPhaseType.workoutPhaseTypeId
            ))
        ).slice().sort(compareBySortOrder)

        if (typePhases.length === 0 && typeBlocks.length === 0) return

        const localPhaseLayouts = []
        let phaseOffsetY = 0
        typePhases.forEach(({ workoutConfig, workoutPhaseConfig, workoutPhaseConfigIndex }) => {
            if (localPhaseLayouts.length > 0) {
                phaseOffsetY += WORKOUT_PHASE_CARD_GAP
            }

            const isExpanded = workoutPhaseConfig.workoutPhaseConfigId === expandedWorkoutPhaseConfigId
            const height = estimateWorkoutPhaseConfigHeight(workoutPhaseConfig, isExpanded)
            localPhaseLayouts.push({
                workoutConfig,
                workoutPhaseConfig,
                workoutPhaseConfigIndex,
                isExpanded,
                x: 0,
                y: phaseOffsetY,
                width: WORKOUT_CONFIG_CARD_WIDTH,
                height,
            })
            phaseOffsetY += height
        })

        const localBlockLayouts = []
        let blockOffsetY = 0
        typeBlocks.forEach((exerciseBlock) => {
            if (localBlockLayouts.length > 0) {
                blockOffsetY += EXERCISE_BLOCK_CARD_GAP
            }

            const metrics = exerciseBlockMetrics[exerciseBlock.exerciseBlockId]
            const height = metrics?.height ?? estimateExerciseBlockCollapsedHeight(exerciseBlock)
            const anchorOffset = metrics?.anchorOffset ?? height / 2
            localBlockLayouts.push({
                exerciseBlock,
                x: 0,
                y: blockOffsetY,
                width: EXERCISE_BLOCK_CARD_WIDTH,
                height,
                anchorOffset,
            })
            blockOffsetY += height
        })

        const phaseColumnHeight = phaseOffsetY
        const blockColumnHeight = blockOffsetY
        const contentHeight = Math.max(phaseColumnHeight, blockColumnHeight)
        const phaseColumnTop = (contentHeight - phaseColumnHeight) / 2
        const blockColumnTop = (contentHeight - blockColumnHeight) / 2
        const contentOriginY = sectionOffsetY

        const workoutPhaseConfigLayouts = localPhaseLayouts.map((layout) => ({
            ...layout,
            x: phaseOriginX,
            y: contentOriginY + phaseColumnTop + layout.y,
        }))
        const exerciseBlockLayouts = localBlockLayouts.map((layout) => ({
            ...layout,
            x: blockOriginX,
            y: contentOriginY + blockColumnTop + layout.y,
        }))

        workoutPhaseConfigLayouts.forEach((phaseLayout) => {
            if (
                hasExpandedPhase
                && phaseLayout.workoutPhaseConfig.workoutPhaseConfigId !== expandedWorkoutPhaseConfigId
            ) return

            exerciseBlockLayouts.forEach((blockLayout) => {
                const x1 = phaseLayout.x + phaseLayout.width
                const y1 = phaseLayout.y + CANVAS_COLLAPSED_CARD_HEIGHT / 2
                const x2 = blockLayout.x
                const y2 = blockLayout.y + blockLayout.anchorOffset

                connectors.push({
                    key: `${phaseLayout.workoutPhaseConfig.workoutPhaseConfigId}-${blockLayout.exerciseBlock.exerciseBlockId}`,
                    x1,
                    y1,
                    x2,
                    y2,
                    path: orthogonalConnectorPath(x1, y1, x2, y2),
                    color: hasExpandedPhase ? CANVAS_CONNECTOR_ACTIVE_COLOR : CANVAS_CONNECTOR_DEFAULT_COLOR,
                })
            })
        })

        workoutPhaseTypeSectionLayouts.push({
            workoutPhaseTypeId: workoutPhaseType.workoutPhaseTypeId,
            name: workoutPhaseType.name || 'Unknown',
            x: 0,
            y: sectionOffsetY,
            width: sectionWidth,
            height: contentHeight,
            workoutPhaseConfigLayouts,
            exerciseBlockLayouts,
        })

        sectionOffsetY += contentHeight + WORKOUT_PHASE_TYPE_SECTION_GAP
    })

    const lastSection = workoutPhaseTypeSectionLayouts[workoutPhaseTypeSectionLayouts.length - 1]
    let unattachedExerciseBlockLayout = null

    if (unattachedExerciseBlock) {
        const metrics = exerciseBlockMetrics[unattachedExerciseBlock.exerciseBlockId]
        const height = metrics?.height ?? estimateExerciseBlockCollapsedHeight(unattachedExerciseBlock)
        const anchorOffset = metrics?.anchorOffset ?? height / 2

        unattachedExerciseBlockLayout = {
            exerciseBlock: unattachedExerciseBlock,
            x: blockOriginX,
            y: lastSection
                ? lastSection.y + lastSection.height + WORKOUT_PHASE_TYPE_SECTION_GAP
                : 0,
            width: EXERCISE_BLOCK_CARD_WIDTH,
            height,
            anchorOffset,
        }
    }

    const contentBottom = unattachedExerciseBlockLayout
        ? unattachedExerciseBlockLayout.y + unattachedExerciseBlockLayout.height
        : (lastSection ? lastSection.y + lastSection.height : 0)

    return {
        workoutPhaseTypeSectionLayouts,
        unattachedExerciseBlockLayout,
        connectors,
        boardWidth: sectionWidth,
        boardHeight: contentBottom,
    }
}

export default function ExerciseCanvasTab({
    viewportRef,
    setTransform = () => {},
    exerciseHashMap = {},
    programMetadata = {},
    workoutConfigs = [],
    exerciseBlocks = [],
    showEquipmentFilteredExercises = false,
    setShowEquipmentFilteredExercises = () => {},
    highlightInjuryOverrides = false,
    setHighlightInjuryOverrides = () => {},
    showHighlightInjuryOverridesSwitch = false,
}) {

    const [portalElement, setPortalElement] = useState(null)
    const [exerciseBlockMetrics, setExerciseBlockMetrics] = useState({})
    const [expandedWorkoutPhaseConfigId, setExpandedWorkoutPhaseConfigId] = useState(null)

    const onExerciseBlockHeightChange = useCallback((exerciseBlockId, height, anchorOffset) => {
        setExerciseBlockMetrics((current) => {
            const existing = current[exerciseBlockId]
            if (existing?.height === height && existing?.anchorOffset === anchorOffset) return current
            return {
                ...current,
                [exerciseBlockId]: { height, anchorOffset },
            }
        })
    }, [])

    const onWorkoutPhaseConfigToggle = useCallback((workoutPhaseConfigId) => {
        setExpandedWorkoutPhaseConfigId((current) => (
            current === workoutPhaseConfigId ? null : workoutPhaseConfigId
        ))
    }, [])

    useLayoutEffect(() => {
        setPortalElement(viewportRef?.current ?? null)
    }, [viewportRef])

    useEffect(() => {
        if (
            workoutConfigs === null
            || exerciseBlocks === null
            || programMetadata === null
            || Object.keys(exerciseHashMap).length === 0
        ) {
            return
        }

        const viewport = viewportRef?.current
        if (!viewport) return

        const { workoutPhaseTypes, workoutPhaseTypeHashMap } = programMetadata
        const { boardWidth, boardHeight } = buildExercisesViewLayout(
            workoutConfigs,
            exerciseBlocks,
            {},
            null,
            workoutPhaseTypes,
            workoutPhaseTypeHashMap,
        )

        const { width, height } = viewport.getBoundingClientRect()

        setTransform({
            x: Math.max(0, width / 2 - boardWidth / 2),
            y: Math.max(0, height / 2 - boardHeight / 2 + INITIAL_MOUNT_Y_OFFSET),
            zoom: 1,
        })
    }, [workoutConfigs, exerciseBlocks, programMetadata, exerciseHashMap, viewportRef, setTransform])

    if (
        workoutConfigs === null
        || exerciseBlocks === null
        || programMetadata === null
        || Object.keys(exerciseHashMap).length === 0
    ) {
        return null
    }

    const { programLevelHashMap, workoutPhaseTypes, workoutPhaseTypeHashMap } = programMetadata
    const { workoutPhaseTypeSectionLayouts, unattachedExerciseBlockLayout, connectors, boardWidth, boardHeight } = buildExercisesViewLayout(
        workoutConfigs,
        exerciseBlocks,
        exerciseBlockMetrics,
        expandedWorkoutPhaseConfigId,
        workoutPhaseTypes,
        workoutPhaseTypeHashMap,
    )

    const showEquipmentFilteredExercisesSwitch = portalElement && createPortal(
        <div
            className="absolute top-[4.5rem] left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-y-2"
            onPointerDown={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
        >
            <div className="flex items-center gap-x-3 rounded-md border border-zinc-200 bg-white/90 px-3 py-1.5 text-sm text-zinc-700 shadow-sm backdrop-blur">
                <span>Show equipment filtered exercises</span>

                <Switch
                    checked={showEquipmentFilteredExercises}
                    onChange={setShowEquipmentFilteredExercises}
                    className={`
                        relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                        focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2
                        ${showEquipmentFilteredExercises ? 'bg-orange-400' : 'bg-zinc-200'}
                    `}
                >
                    <span className="sr-only">Show equipment filtered exercises</span>
                    <span
                        aria-hidden="true"
                        className={`
                            pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                            ${showEquipmentFilteredExercises ? 'translate-x-4' : 'translate-x-0'}
                        `}
                    />
                </Switch>
            </div>

            {showHighlightInjuryOverridesSwitch && (
                <div className="flex items-center gap-x-3 rounded-md border border-zinc-200 bg-white/90 px-3 py-1.5 text-sm text-zinc-700 shadow-sm backdrop-blur">
                    <span>Highlight injury overrides</span>

                    <Switch
                        checked={highlightInjuryOverrides}
                        onChange={setHighlightInjuryOverrides}
                        data-testid='highlight-injury-overrides-switch'
                        className={`
                            relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                            focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2
                            ${highlightInjuryOverrides ? 'bg-orange-400' : 'bg-zinc-200'}
                        `}
                    >
                        <span className="sr-only">Highlight injury overrides</span>
                        <span
                            aria-hidden="true"
                            className={`
                                pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                                ${highlightInjuryOverrides ? 'translate-x-4' : 'translate-x-0'}
                            `}
                        />
                    </Switch>
                </div>
            )}
        </div>,
        portalElement,
    )

    return (
        <>
            {showEquipmentFilteredExercisesSwitch}

            <div
                className="absolute left-0 top-0"
                style={{
                    width: boardWidth,
                    height: boardHeight,
                }}
            >
                {workoutPhaseTypeSectionLayouts.map((section) => (
                    <Fragment key={section.workoutPhaseTypeId}>
                        {section.workoutPhaseConfigLayouts.map(({ workoutConfig, workoutPhaseConfig, workoutPhaseConfigIndex, isExpanded, x, y, width }) => (
                            <div
                                key={workoutPhaseConfig.workoutPhaseConfigId ?? `${workoutConfig.workoutConfigId}-${workoutPhaseConfigIndex}`}
                                className="absolute overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md"
                                style={{
                                    left: x,
                                    top: y,
                                    width,
                                }}
                                onPointerDown={(event) => event.stopPropagation()}
                            >
                                <div
                                    className={`
                                        p-4 flex items-center gap-x-4 cursor-pointer
                                        ${isExpanded ? 'border-b border-gray-200' : ''}
                                    `}
                                    onClick={() => onWorkoutPhaseConfigToggle(workoutPhaseConfig.workoutPhaseConfigId)}
                                >
                                    <p className="text-left text-base font-medium text-gray-900 flex-1">
                                        {workoutPhaseConfig.name}
                                    </p>

                                    <p className='text-xs font-bold text-orange-400'>
                                        {workoutPhaseTypeHashMap[workoutPhaseConfig.workoutPhaseTypeId]?.name || 'Unknown'}
                                    </p>

                                    {isExpanded && <ChevronUpIcon className='w-6 h-6 text-gray-600' />}
                                    {!isExpanded && <ChevronDownIcon className='w-6 h-6 text-gray-600' />}
                                </div>

                                {isExpanded &&
                                    <div className="space-y-2 p-4">
                                        {workoutPhaseConfig.workoutSlotConfigs.map((_, workoutSlotIndex) => (
                                            <div
                                                key={workoutSlotIndex}
                                                className="rounded-xl bg-gray-100 px-4 py-2"
                                            >
                                                <p className="text-center text-sm text-gray-600">
                                                    Slot {workoutSlotIndex + 1}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                }
                            </div>
                        ))}

                        {section.exerciseBlockLayouts.map(({ exerciseBlock, x, y, width }) => (
                            <div
                                key={exerciseBlock.exerciseBlockId}
                                className="absolute"
                                style={{
                                    left: x,
                                    top: y,
                                    width,
                                }}
                            >
                                <ExerciseBlock
                                    exerciseHashMap={exerciseHashMap}
                                    programLevelHashMap={programLevelHashMap}
                                    workoutPhaseTypeHashMap={workoutPhaseTypeHashMap}
                                    programType='config'
                                    exerciseBlock={exerciseBlock}
                                    enabledCanvasStyling={true}
                                    onHeightChange={onExerciseBlockHeightChange}
                                    showEquipmentFilteredExercises={showEquipmentFilteredExercises}
                                    highlightInjuryOverrides={highlightInjuryOverrides}
                                />
                            </div>
                        ))}
                    </Fragment>
                ))}

                {unattachedExerciseBlockLayout && (
                    <div
                        className="absolute"
                        style={{
                            left: unattachedExerciseBlockLayout.x,
                            top: unattachedExerciseBlockLayout.y,
                            width: unattachedExerciseBlockLayout.width,
                        }}
                    >
                        <ExerciseBlock
                            exerciseHashMap={exerciseHashMap}
                            programLevelHashMap={programLevelHashMap}
                            workoutPhaseTypeHashMap={{
                                ...workoutPhaseTypeHashMap,
                                null: { name: 'Unattached' },
                            }}
                            programType='config'
                            exerciseBlock={unattachedExerciseBlockLayout.exerciseBlock}
                            enabledCanvasStyling={true}
                            onHeightChange={onExerciseBlockHeightChange}
                            showEquipmentFilteredExercises={showEquipmentFilteredExercises}
                            highlightInjuryOverrides={highlightInjuryOverrides}
                        />
                    </div>
                )}

                <svg
                    className="pointer-events-none absolute left-0 top-0 z-10 overflow-visible"
                    width={boardWidth}
                    height={boardHeight}
                    aria-hidden="true"
                >
                    {connectors.map((connector) => (
                        <g key={connector.key}>
                            <path
                                d={connector.path}
                                fill="none"
                                stroke={connector.color}
                                strokeWidth="1.5"
                            />
                            <circle
                                cx={connector.x1}
                                cy={connector.y1}
                                r="3.5"
                                fill="#f4f4f5"
                                stroke={connector.color}
                                strokeWidth="1.5"
                            />
                            <circle
                                cx={connector.x2}
                                cy={connector.y2}
                                r="3.5"
                                fill="#f4f4f5"
                                stroke={connector.color}
                                strokeWidth="1.5"
                            />
                        </g>
                    ))}
                </svg>
            </div>
        </>
    )
}
